import { makeAutoObservable } from 'mobx'
import { type INormalizedApiError } from 'shared/api'
import {
  AgentActions,
  type IAgentActionSettings,
  type IAgentActionType,
} from 'entities/AgentActions'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type ISurveySettings } from 'entities/Surveys/api/types'
import { AgentActionsControl } from 'widgets/AgentActions'
import { SuccessActionsErrors } from './successActionsErrors'
import { CategoryActionsStore } from './categoryActionsStore'

export class SuccessActionsStore {
  private readonly _model = new AgentActions()
  private readonly _errors = new SuccessActionsErrors('success_action')
  private readonly _control: AgentActionsControl
  private readonly _promoters: CategoryActionsStore
  private readonly _passives: CategoryActionsStore
  private readonly _detractors: CategoryActionsStore
  private _isDifferentActions = false

  constructor(entities: IEntitiesStore) {
    this._control = new AgentActionsControl(this._model, entities)
    this._promoters = new CategoryActionsStore(entities, 'success_action_promoters')
    this._passives = new CategoryActionsStore(entities, 'success_action_passives')
    this._detractors = new CategoryActionsStore(entities, 'success_action_detractors')
    makeAutoObservable(this)
  }

  init = (settings: ISurveySettings | null | undefined) => {
    this._isDifferentActions = settings?.success_action_per_group?.value ?? false
    this._model.syncOrigin(settings?.success_action?.value ?? [])
    this._promoters.init(settings?.success_action_promoters?.value)
    this._passives.init(settings?.success_action_passives?.value)
    this._detractors.init(settings?.success_action_detractors?.value)
    this._errors.clear()
  }

  get control() {
    return this._control
  }

  get errors() {
    return this._errors
  }

  get promoters() {
    return this._promoters
  }

  get passives() {
    return this._passives
  }

  get detractors() {
    return this._detractors
  }

  get hasActions() {
    return this._control.list.length > 0
  }

  get sectionError() {
    if (this._isDifferentActions) {
      return (
        this._promoters.sectionError ?? this._passives.sectionError ?? this._detractors.sectionError
      )
    }
    return this._errors.sectionError
  }

  get isDifferentActions() {
    return this._isDifferentActions
  }

  get params() {
    if (this._isDifferentActions) {
      return {
        success_action: { value: null },
        success_action_per_group: { value: true },
        success_action_promoters: { value: this._promoters.paramsValue },
        success_action_passives: { value: this._passives.paramsValue },
        success_action_detractors: { value: this._detractors.paramsValue },
      }
    }

    return {
      success_action: { value: this.paramsValue },
      success_action_per_group: { value: false },
      success_action_promoters: { value: null },
      success_action_passives: { value: null },
      success_action_detractors: { value: null },
    }
  }

  createAction = (type: IAgentActionType) => {
    this._control.createAction(type)
  }

  syncApiErrors = (apiErrors: INormalizedApiError | null | undefined) => {
    this._errors.sync(apiErrors, this._model)
    this._promoters.syncApiErrors(apiErrors)
    this._passives.syncApiErrors(apiErrors)
    this._detractors.syncApiErrors(apiErrors)
  }

  resetSectionError = () => {
    if (this._errors.hasOwnSectionError) this._errors.removeSectionError()
  }

  setIsDifferentActions = (value: boolean) => {
    this._isDifferentActions = value
  }

  dispose = () => {
    this._control.dispose()
    this._promoters.dispose()
    this._passives.dispose()
    this._detractors.dispose()
  }

  get paramsValue(): IAgentActionSettings[] | null {
    const list = this._model.toJSON()
    return list.length > 0 ? list : null
  }
}
